import React, { forwardRef, useState } from "react";

// The file named the school: one line, editable on request. No form unless asked for.
const FoundSchool = forwardRef(function FoundSchool({ prompt, schoolName, schoolAddress, onSchoolName, onSchoolAddress }, ref) {
  const [editing, setEditing] = useState(false);
  const others = prompt.schools.slice(1);
  return (
    <section className="start-school-decision start-school-decision--found start-school-decision--line" ref={ref}>
      <div className="start-school-line">
        <span className="start-school-line__label">School</span>
        {editing ? (
          <div className="start-school-line__fields">
            <input
              type="text"
              value={schoolName}
              aria-label="School name"
              onChange={(event) => onSchoolName(event.target.value)}
              autoFocus
            />
            <input
              type="text"
              value={schoolAddress}
              aria-label="School address"
              placeholder="Address (optional)"
              onChange={(event) => onSchoolAddress(event.target.value)}
            />
          </div>
        ) : (
          <span className="start-school-line__value">
            <strong>{schoolName || prompt.schools[0]?.name}</strong>
            {schoolAddress ? ` — ${schoolAddress}` : ""}
          </span>
        )}
        <button type="button" className="start-text-link" onClick={() => setEditing((current) => !current)}>
          {editing ? "Done" : "Edit"}
        </button>
      </div>
      {others.length > 0 && (
        <p className="start-muted">
          Also in the file: {others.map((school) => school.name).join(", ")}. Each passenger keeps the school in their row.
        </p>
      )}
    </section>
  );
});

const SchoolDecision = forwardRef(function SchoolDecision(
  { prompt, schoolName, schoolAddress, schoolDecision, onSchoolName, onSchoolAddress, onSchoolDecision, blocked, companySchools = [], required },
  ref,
) {
  const [schoolMenuOpen, setSchoolMenuOpen] = useState(false);
  if (!prompt) return null;

  if (prompt.status === "found") {
    return (
      <FoundSchool
        ref={ref}
        prompt={prompt}
        schoolName={schoolName}
        schoolAddress={schoolAddress}
        onSchoolName={onSchoolName}
        onSchoolAddress={onSchoolAddress}
      />
    );
  }

  const pickExisting = (event) => {
    const chosen = companySchools.find((school) => school.id === event.target.value);
    if (!chosen) return;
    onSchoolDecision("existing");
    onSchoolName(chosen.name);
    onSchoolAddress(chosen.address || "");
  };

  return (
    <section
      id="start-school-decision"
      ref={ref}
      className={`start-school-decision start-school-decision--required${blocked ? " is-blocking" : ""}`}
    >
      {blocked && <p className="start-school-needed">Needed to continue</p>}
      <h3>No school was found</h3>
      <p>
        {required
          ? "These passengers need a school before anything can be created. Choose an option below — Continue stays off until you do."
          : "These services need a school. Choose one below."}
      </p>
      <div className="start-choice-row">
        {companySchools.length > 0 && (
          <div className="start-school-menu">
            <button
              type="button"
              role="combobox"
              className="start-choice"
              aria-label="Use one of your schools"
              aria-haspopup="listbox"
              aria-expanded={schoolMenuOpen}
              onClick={() => setSchoolMenuOpen((open) => !open)}
            >
              {schoolDecision === "existing" && schoolName ? schoolName : "Use one of my schools…"}
            </button>
            {schoolMenuOpen && (
              <ul className="start-school-menu__list" role="listbox" aria-label="Use one of your schools">
                {companySchools.map((school) => (
                  <li key={school.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={schoolDecision === "existing" && school.name === schoolName}
                      onClick={() => {
                        pickExisting({ target: { value: school.id } });
                        setSchoolMenuOpen(false);
                      }}
                    >
                      {school.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button
          type="button"
          className={`start-choice ${schoolDecision === "create" ? "is-selected" : ""}`}
          aria-pressed={schoolDecision === "create"}
          onClick={() => onSchoolDecision("create")}
        >
          Create a school now
        </button>
        {required && (
          <button
            type="button"
            className={`start-choice ${schoolDecision === "later" ? "is-selected" : ""}`}
            aria-pressed={schoolDecision === "later"}
            onClick={() => onSchoolDecision("later")}
          >
            Continue — I’ll say which school later
          </button>
        )}
      </div>
      {schoolDecision === "existing" && schoolName && (
        <p className="start-note">
          Using <strong>{schoolName}</strong>
          {schoolAddress ? ` — ${schoolAddress}` : ""}
        </p>
      )}
      {schoolDecision === "create" && (
        <>
          <label>
            <span>School name</span>
            <input
              type="text"
              value={schoolName}
              onChange={(event) => onSchoolName(event.target.value)}
              placeholder="e.g. Demet Ortaokulu"
              autoFocus
            />
          </label>
          <label>
            <span>School address (optional)</span>
            <input
              type="text"
              value={schoolAddress}
              onChange={(event) => onSchoolAddress(event.target.value)}
              placeholder="Street, neighborhood, city"
            />
          </label>
        </>
      )}
    </section>
  );
});

export default SchoolDecision;
